import { auth } from "@/firebaseConfig";

/**
 * Wait for Firebase auth to settle before reading `auth.currentUser`.
 *
 * On client-side route navigation, Vue mounts a new view before
 * Firebase's `onAuthStateChanged` has re-emitted — so `auth.currentUser`
 * is intermittently `null` on the first tick. Any page that reads
 * `.uid` directly in `mounted()` will silently throw or short-circuit,
 * and the member sees an empty page until they hard-reload.
 *
 * This helper returns the user as soon as auth reports one, bounded
 * by a timeout so we never hang forever. Use it in every page that
 * needs `auth.currentUser.uid` at mount time.
 *
 *   async mounted() {
 *     const user = await waitForAuth();
 *     if (!user?.uid) return;
 *     this.userInfo = await userService.getUser(user.uid);
 *   }
 *
 * @param {number} timeoutMs — max wait. Defaults to 2s, which is longer
 *                             than any healthy auth-state-resolve path.
 * @returns {Promise<User|null>}
 */
export function waitForAuth(timeoutMs = 2000) {
  return new Promise((resolve) => {
    if (auth.currentUser) return resolve(auth.currentUser);
    let settled = false;
    const unsub = auth.onAuthStateChanged((user) => {
      if (settled) return;
      settled = true;
      try { unsub(); } catch (e) { /* ignore */ }
      resolve(user || null);
    });
    setTimeout(() => {
      if (settled) return;
      settled = true;
      try { unsub(); } catch (e) { /* ignore */ }
      resolve(auth.currentUser || null);
    }, timeoutMs);
  });
}
